/**
 * Dashboard — the operations view. What is wrong right now, and what is about to be.
 *
 * This screen gets scanned, not read: someone opens it with a coffee in one hand to
 * answer "did last night go out, and is anything about to bite me today?". So it is
 * built to be answerable in about four seconds, and everything on it is encoded in form
 * as well as colour — glyph, border treatment, and a left rule on every bad row — so it
 * survives a greyscale print, a colour-blind reader and a screenshot pasted into a
 * ticket.
 *
 * Five questions, in the order they matter:
 *
 *   1. Did today's runs go out?          today's runs, by state
 *   2. Is anything stale or failed?      stale is NOT failed — the source was missing or
 *                                        older than the window, so nothing was sent
 *                                        (CONTRACT §8.3) and there is a file to chase,
 *                                        not a bug to fix
 *   3. Is anyone's mail bouncing?        a suppressed recipient is silently not receiving
 *                                        reports; nothing else on the console says so
 *   4. What links die in the next day?   our expires_at, not an S3 signature (§1). Catch
 *                                        it before the client hits a 410, not after
 *   5. What is waiting on a human?       an unranked disposition is never silently sorted
 *                                        last (§5) — it sits here until someone rules
 *
 * Every number is a link to the screen that can do something about it, because a
 * dashboard that only tells you the building is on fire is an alarm, not a console.
 *
 * Route: `/dashboard`, first in the Dispatch section of the nav. It was unreachable by any
 * URL or click until the shell mounted it — 1,028 lines of built screen answering five
 * questions, two of which (expiring links, suppressed recipients) have no other surface at
 * all. `/` still lands on /runs: moving the landing page is a product decision.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { Card, PageHeader } from '@/components/Layout';
import Button from '@/components/Button';
import EmptyState from '@/components/EmptyState';
import GrantList, { type GrantRow } from '@/components/GrantList';
import StatTile, { StatGrid, countTone } from '@/components/StatTile';
import Table, { type Column } from '@/components/Table';
import { ActiveBadge, Badge, CoverageBadge, StatusBadge } from '@/components/Badge';
import { useToast } from '@/components/Toast';
import { api, errorMessage } from '@/lib/api';
import {
  coverageOf,
  formatCount,
  isStalledRun,
  istDate,
  istDateTime,
  istDayKey,
  istToday,
  linkState,
  nowIstLabel,
  runWindowLabel,
  stalledFor,
  timeAgo,
  toDate,
} from '@/lib/format';
import {
  RUN_IN_FLIGHT,
  reportShapeInfo,
  type BounceState,
  type Client,
  type DispositionConfig,
  type Recipient,
  type Run,
  type Schedule,
} from '@/lib/types';

// ---------------------------------------------------------------------------
// Tuning
// ---------------------------------------------------------------------------

/** How many recent runs we pull to answer "today" and "recently broken". */
const RUN_SCAN_LIMIT = 100;
/** How far back a failure still counts as needing attention. */
const ATTENTION_DAYS = 3;
/* STUCK_MINUTES lives in lib/format: /runs, /runs/:id and this screen must agree on when
   a run has stalled, and a threshold defined privately here was one nobody else could
   reach — so the detection shipped to one screen, which until now no URL reached. */
/** Grants only live 7 days, so nothing older than this can still be expiring. */
const GRANT_LOOKBACK_DAYS = 8;
/** Grant reads cost one query per run; bound them. */
const GRANT_RUN_LIMIT = 12;
/** Background refresh. Long enough not to hammer the API from an idle wall display. */
const REFRESH_MS = 60_000;

const MS_PER_MINUTE = 60_000;
const MS_PER_DAY = 86_400_000;

// ---------------------------------------------------------------------------
// Wire extras
// ---------------------------------------------------------------------------

interface RunRow extends Run {
  client_name?: string;
}

interface UnrankedEntry {
  value: string;
  seen_count?: number;
  run_count?: number;
  first_seen_at?: string;
  last_seen_at?: string;
}

interface DispositionRow extends DispositionConfig {
  client_name?: string;
  unranked: UnrankedEntry[];
}

interface SuppressedRow extends Recipient {
  client_name?: string;
}

/** One thing that wants a human, whatever its shape. */
interface Attention {
  id: string;
  run: RunRow;
  kind: 'failed' | 'stale' | 'stuck';
  reason: string;
}

const BOUNCE_SPEC: Record<BounceState, { tone: 'ok' | 'warn' | 'danger'; glyph: string; title: string }> = {
  ok: { tone: 'ok', glyph: '✓', title: 'Delivering normally' },
  soft: {
    tone: 'warn',
    glyph: '▲',
    title: 'Soft bounce — a full mailbox or a temporary rejection. Watch it; it may clear.',
  },
  hard: {
    tone: 'danger',
    glyph: '✕',
    title: 'Hard bounce — the address does not exist. SES suppresses it; reports are not reaching this person.',
  },
  complained: {
    tone: 'danger',
    glyph: '⚑',
    title: 'Marked our mail as spam. Sending again risks the whole domain reputation.',
  },
};

function withinDays(value: string | null | undefined, days: number, now: Date): boolean {
  const date = toDate(value);
  if (!date) return false;
  return now.getTime() - date.getTime() <= days * MS_PER_DAY;
}

/* runWindowLabel moved to lib/format. /runs used to render the schedule SPEC word for the
   same run this screen rendered as resolved IST dates, and an operator reconciling the two
   had no way to know they agreed. One derivation, both screens. */

// ---------------------------------------------------------------------------
// Screen
// ---------------------------------------------------------------------------

export default function Dashboard() {
  const toast = useToast();
  const [now, setNow] = useState(() => new Date());

  const [clients, setClients] = useState<Client[]>([]);
  const [runs, setRuns] = useState<RunRow[]>([]);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [suppressed, setSuppressed] = useState<SuppressedRow[]>([]);
  const [grants, setGrants] = useState<GrantRow[]>([]);
  const [dispositions, setDispositions] = useState<DispositionRow[]>([]);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [fatal, setFatal] = useState<unknown>(null);
  /**
      * Per-section failures. One dead endpoint must not blank the other four answers.
      *
      * Named `partialLoads`, not `partial`: this file now also renders partial-COVERAGE
      * marks, and those mean a run whose source stopped early. A bare `partial` sitting in
      * the same component as `coverageOf(run).isPartial` is a trap for the next reader.
      */
  const [partialLoads, setPartialLoads] = useState<string[]>([]);
  const [loadedAt, setLoadedAt] = useState<Date | null>(null);

  const toastRef = useRef(toast);
  toastRef.current = toast;

  const load = useCallback(async (signal: AbortSignal, background: boolean) => {
    if (background) setRefreshing(true);
    const failures: string[] = [];

    const [clientsResult, runsResult, schedulesResult] = await Promise.allSettled([
      api.clients.listAll({ signal }),
      api.runs.list({ limit: RUN_SCAN_LIMIT }, { signal }),
      api.schedules.list({ limit: 100 }, { signal }),
    ]);
    if (signal.aborted) return;

    const clientList = clientsResult.status === 'fulfilled' ? clientsResult.value : [];
    if (clientsResult.status === 'rejected') failures.push('clients');
    else setClients(clientList);

    const runList =
      runsResult.status === 'fulfilled' ? (runsResult.value.items as RunRow[]) : [];
    if (runsResult.status === 'rejected') {
      failures.push('runs');
      // Runs are the spine of this screen: without them there is nothing to show.
      if (!background) setFatal(runsResult.reason);
    } else {
      setRuns(runList);
      setFatal(null);
    }

    if (schedulesResult.status === 'fulfilled') setSchedules(schedulesResult.value.items);
    else failures.push('schedules');

    const clientNames = new Map(clientList.map((c) => [c.client_id, c.name] as const));

    // --- recipients: who is silently not receiving anything ------------------
    const recipientResults = await Promise.allSettled(
      clientList.map((c) => api.recipients.list(c.client_id, { limit: 200 }, { signal })),
    );
    if (signal.aborted) return;
    const bad: SuppressedRow[] = [];
    let recipientFailed = false;
    recipientResults.forEach((result) => {
      if (result.status !== 'fulfilled') {
        recipientFailed = true;
        return;
      }
      for (const recipient of result.value.items) {
        if (recipient.bounce_state && recipient.bounce_state !== 'ok') {
          bad.push({ ...recipient, client_name: clientNames.get(recipient.client_id) });
        } else if (recipient.suppressed_at) {
          bad.push({ ...recipient, client_name: clientNames.get(recipient.client_id) });
        }
      }
    });
    if (recipientFailed) failures.push('recipients');
    setSuppressed(bad);

    // --- dispositions: what is waiting on a ruling ---------------------------
    const dispositionResults = await Promise.allSettled(
      clientList.map((c) => api.dispositions.get(c.client_id, { signal })),
    );
    if (signal.aborted) return;
    const configs: DispositionRow[] = [];
    let dispositionFailed = false;
    dispositionResults.forEach((result, index) => {
      if (result.status !== 'fulfilled') {
        dispositionFailed = true;
        return;
      }
      const row = result.value as DispositionRow;
      const unranked = (row.unranked ?? []) as UnrankedEntry[];
      if (unranked.length > 0) {
        configs.push({
          ...row,
          unranked,
          client_name: row.client_name ?? clientList[index]?.name ?? row.client_id,
        });
      }
    });
    if (dispositionFailed) failures.push('dispositions');
    setDispositions(configs);

    // --- grants: what stops working in the next 24 hours ---------------------
    const reference = new Date();
    const recentRuns = runList
      .filter((run) => (run.artifacts?.length ?? 0) > 0)
      .filter((run) => withinDays(run.started_at, GRANT_LOOKBACK_DAYS, reference))
      .slice(0, GRANT_RUN_LIMIT);

    const grantResults = await Promise.allSettled(
      recentRuns.map((run) => api.runs.grants(run.run_id, { limit: 100 }, { signal })),
    );
    if (signal.aborted) return;
    const allGrants: GrantRow[] = [];
    let grantFailed = false;
    grantResults.forEach((result) => {
      if (result.status !== 'fulfilled') {
        grantFailed = true;
        return;
      }
      allGrants.push(...(result.value.items as GrantRow[]));
    });
    if (grantFailed) failures.push('links');
    setGrants(allGrants);

    setPartialLoads(failures);
    setLoadedAt(new Date());
    setNow(new Date());
    setLoading(false);
    setRefreshing(false);
  }, []);

  // First load.
  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal, false);
    return () => controller.abort();
  }, [load]);

  // Background refresh, paused while the tab is hidden so a wall display does not spend
  // the night polling.
  const [manualKey, setManualKey] = useState(0);
  useEffect(() => {
    if (manualKey === 0) return undefined;
    const controller = new AbortController();
    void load(controller.signal, true);
    return () => controller.abort();
  }, [manualKey, load]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'hidden') return;
      setManualKey((k) => k + 1);
    }, REFRESH_MS);
    return () => window.clearInterval(timer);
  }, []);

  // The countdown labels ("Expires in 3 h") are only honest if the clock advances.
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), MS_PER_MINUTE);
    return () => window.clearInterval(timer);
  }, []);

  // --- derived -------------------------------------------------------------
  const today = istToday(now);

  const todayRuns = useMemo(
    () => runs.filter((run) => istDayKey(run.started_at) === today),
    [runs, today],
  );

  /**
   * Genuinely moving, not merely marked so. A run that has said "generating" for 21 hours
   * is not in flight; counting it as such is the same claim the pulsing badge used to make.
   */
  const inFlight = useMemo(
    () =>
      runs.filter(
        (run) => RUN_IN_FLIGHT.includes(run.status) && !isStalledRun(run.status, run.started_at, now),
      ),
    [runs, now],
  );

  const attention = useMemo<Attention[]>(() => {
    const rows: Attention[] = [];
    for (const run of runs) {
      if (!withinDays(run.started_at, ATTENTION_DAYS, now)) continue;
      if (run.status === 'failed') {
        rows.push({
          id: `${run.run_id}:failed`,
          run,
          kind: 'failed',
          reason: run.error || 'No error recorded on the run.',
        });
      } else if (run.status === 'stale') {
        rows.push({
          id: `${run.run_id}:stale`,
          run,
          kind: 'stale',
          reason:
            run.error ||
            'The source object was missing or older than the window, so nothing was sent. Chase the export, not the dispatcher.',
        });
      } else if (isStalledRun(run.status, run.started_at, now)) {
        rows.push({
          id: `${run.run_id}:stuck`,
          run,
          kind: 'stuck',
          reason: `Still ${run.status} after ${stalledFor(run.started_at, now)} with no progress. The dispatcher may have died mid-run.`,
        });
      }
    }
    return rows.sort((a, b) => String(b.run.started_at).localeCompare(String(a.run.started_at)));
  }, [runs, now]);

  const expiringGrants = useMemo(
    () => grants.filter((g) => linkState(g, now).state === 'expiring'),
    [grants, now],
  );
  const liveGrants = useMemo(
    () => grants.filter((g) => linkState(g, now).state === 'live'),
    [grants, now],
  );

  const hardBounces = suppressed.filter(
    (r) => r.bounce_state === 'hard' || r.bounce_state === 'complained',
  );
  const softBounces = suppressed.filter((r) => r.bounce_state === 'soft');

  const unrankedTotal = dispositions.reduce((sum, row) => sum + row.unranked.length, 0);

  /** One sentence, four tiles: whatever the runs fetch could not tell us, it could not tell us. */
  const runsUnknown =
    partialLoads.includes('runs') &&
    'The runs fetch failed on this refresh, so this is not a count. Unknown, not zero.';

  const sentToday = todayRuns.filter((r) => r.status === 'sent').length;
  const failedToday = todayRuns.filter((r) => r.status === 'failed').length;
  const staleToday = todayRuns.filter((r) => r.status === 'stale').length;

  const nextSchedule = useMemo(() => {
    const upcoming = schedules
      .filter((s) => s.enabled && s.next_run_at)
      .sort((a, b) => String(a.next_run_at).localeCompare(String(b.next_run_at)));
    return upcoming[0] ?? null;
  }, [schedules]);

  const clientName = useCallback(
    (clientId: string) => clients.find((c) => c.client_id === clientId)?.name ?? clientId,
    [clients],
  );

  const manualRefresh = () => {
    setManualKey((k) => k + 1);
  };

  // --- tables --------------------------------------------------------------
  const attentionColumns: Column<Attention>[] = [
    {
      key: 'state',
      header: 'State',
      width: '124px',
      nowrap: true,
      render: (row) => <StatusBadge status={row.run.status} startedAt={row.run.started_at} now={now} />,
    },
    {
      key: 'client',
      header: 'Client',
      width: '180px',
      render: (row) => (
        <Link
          className="truncate"
          style={{ display: 'block' }}
          to={`/clients/${encodeURIComponent(row.run.client_id)}`}
          title={`${row.run.client_name || clientName(row.run.client_id)} · ${row.run.client_id}`}
        >
          {row.run.client_name || clientName(row.run.client_id)}
        </Link>
      ),
    },
    {
      key: 'window',
      header: 'Window (IST)',
      width: '176px',
      nowrap: true,
      render: (row) => <span>{runWindowLabel(row.run)}</span>,
    },
    {
      key: 'started',
      header: 'Started (IST)',
      width: '165px',
      nowrap: true,
      render: (row) => (
        <span title={timeAgo(row.run.started_at, now)}>{istDateTime(row.run.started_at)}</span>
      ),
    },
    {
      /* The evidence column: no width, so fixed layout hands it every remaining pixel, and
         no truncate() — this table exists to say why something needs a human, and the tail
         of the sentence used to be discarded with no tooltip and no second copy anywhere. */
      key: 'reason',
      header: 'Why',
      wrap: true,
      render: (row) => <span>{row.reason}</span>,
    },
    {
      key: 'open',
      header: '',
      width: '84px',
      align: 'right',
      nowrap: true,
      render: (row) => (
        <Link className="btn btn-sm" style={{ textDecoration: 'none' }} to={`/runs/${encodeURIComponent(row.run.run_id)}`}>
          Open
        </Link>
      ),
    },
  ];

  const runColumns: Column<RunRow>[] = [
    {
      key: 'status',
      header: 'Status',
      width: '124px',
      nowrap: true,
      render: (run) => (
        <div className="stack-tight" style={{ alignItems: 'flex-start' }}>
          <StatusBadge status={run.status} startedAt={run.started_at} now={now} />
          <CoverageBadge coverage={coverageOf(run)} />
        </div>
      ),
    },
    {
      key: 'client',
      header: 'Client',
      /* Deliberately width-LESS, and the only column here that is.
         Under `table-layout: fixed` the declared widths bind, so a table whose every
         column declares one has nothing to absorb the slack: narrower than the sum and
         it overflows sideways, wider and the browser stretches columns that asked not to
         be. Exactly one column takes the remainder, and it must be one that truncates
         gracefully — which the client name does. */
      render: (run) => (
        <Link
          className="truncate"
          to={`/clients/${encodeURIComponent(run.client_id)}`}
          title={`${run.client_name || clientName(run.client_id)} · ${run.client_id}`}
        >
          {run.client_name || clientName(run.client_id)}
        </Link>
      ),
    },
    {
      key: 'shape',
      header: 'Shape',
      width: '104px',
      nowrap: true,
      render: (run) => (
        <Badge
          variant="plain"
          glyph={reportShapeInfo(run.report_shape).glyph}
          title={
            run.deliver === false
              ? 'Internal download — never mailed to the client'
              : reportShapeInfo(run.report_shape).hint
          }
        >
          {run.report_shape ?? 'borrower'}
          {run.deliver === false ? ' · internal' : ''}
        </Badge>
      ),
    },
    {
      key: 'window',
      header: 'Window (IST)',
      width: '190px',
      nowrap: true,
      headerTitle:
        'For a partial run this is the coverage the source actually had, not the window that was asked for.',
      render: (run) => <span className="small">{runWindowLabel(run)}</span>,
    },
    {
      key: 'rows',
      header: 'Rows out',
      width: '96px',
      align: 'right',
      mono: true,
      render: (run) => formatCount(run.rows_out),
    },
    {
      key: 'recipients',
      header: 'Recipients',
      width: '96px',
      align: 'right',
      mono: true,
      headerTitle: 'Recipients on the run row',
      render: (run) =>
        run.deliver === false ? (
          <span className="dim" title="Internal download — never mailed to the client">
            —
          </span>
        ) : (
          formatCount(run.recipients?.length ?? 0)
        ),
    },
    {
      key: 'started',
      header: 'Started (IST)',
      width: '165px',
      nowrap: true,
      /* One line. The relative time is a supplement (CONTRACT §8.7 forbids relative-ONLY),
         so it moves to the tooltip and the absolute value stays on screen. */
      render: (run) => <span title={timeAgo(run.started_at, now)}>{istDateTime(run.started_at)}</span>,
    },
    {
      key: 'open',
      header: '',
      width: '84px',
      align: 'right',
      nowrap: true,
      render: (run) => (
        <Link className="btn btn-sm" style={{ textDecoration: 'none' }} to={`/runs/${encodeURIComponent(run.run_id)}`}>
          Open
        </Link>
      ),
    },
  ];

  const suppressedColumns: Column<SuppressedRow>[] = [
    {
      key: 'state',
      header: 'Bounce',
      width: '132px',
      nowrap: true,
      render: (row) => {
        const spec = BOUNCE_SPEC[row.bounce_state] ?? BOUNCE_SPEC.ok;
        return (
          <Badge
            tone={spec.tone}
            glyph={spec.glyph}
            terminalBad={row.bounce_state === 'hard' || row.bounce_state === 'complained'}
            pending={row.bounce_state === 'soft'}
            title={spec.title}
          >
            {row.bounce_state}
          </Badge>
        );
      },
    },
    {
      key: 'client',
      header: 'Client',
      width: '180px',
      render: (row) => (
        <Link
          className="truncate"
          style={{ display: 'block' }}
          to={`/clients/${encodeURIComponent(row.client_id)}`}
          title={`${row.client_name || clientName(row.client_id)} · ${row.client_id}`}
        >
          {row.client_name || clientName(row.client_id)}
        </Link>
      ),
    },
    {
      /* The address IN FULL. This table exists because "a suppressed address is skipped at
         send time and nothing else on this console says so" — and the address was the one
         thing on the row that was masked. maskEmail's own docstring says it is for logs and
         that screens may show addresses in full; on a client with two people at the same
         domain, `s••@…` is genuinely ambiguous. It is the width-less column, so it wraps
         rather than clipping, and the name rides along in the tooltip. */
      key: 'recipient',
      header: 'Recipient',
      wrap: true,
      mono: true,
      render: (row) => <span title={row.name || '(no name)'}>{row.email}</span>,
    },
    {
      key: 'kind',
      header: 'Kind',
      width: '80px',
      nowrap: true,
      render: (row) => (
        <Badge variant="plain" glyph="·">
          {row.kind}
        </Badge>
      ),
    },
    {
      key: 'active',
      header: 'Listed',
      width: '104px',
      nowrap: true,
      render: (row) => <ActiveBadge active={row.active} />,
    },
    {
      key: 'since',
      header: 'Suppressed (IST)',
      width: '176px',
      nowrap: true,
      optional: true,
      render: (row) =>
        row.suppressed_at ? (
          <span title={timeAgo(row.suppressed_at, now)}>{istDateTime(row.suppressed_at)}</span>
        ) : (
          <span className="dim">not suppressed</span>
        ),
    },
  ];

  // ---------------------------------------------------------------------------

  if (fatal) {
    return (
      <div className="stack">
        <PageHeader title="Operations" subtitle={nowIstLabel(now)} />
        <EmptyState
          tone="danger"
          title="Could not load the dashboard"
          description={errorMessage(fatal)}
          action={
            <Button variant="primary" onClick={manualRefresh}>
              Retry
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="stack">
      <PageHeader
        title="Operations"
        subtitle={
          <>
            {nowIstLabel(now)}
            {loadedAt ? (
              <span className="dim"> · data as of {istDateTime(loadedAt)}</span>
            ) : null}
          </>
        }
        actions={
          <Button onClick={manualRefresh} loading={refreshing} icon="↻">
            Refresh
          </Button>
        }
      />

      {partialLoads.length > 0 ? (
        <div className="callout tone-warn" role="status">
          <span className="callout-glyph" aria-hidden="true">
            ▲
          </span>
          <span>
            Part of this page did not load: {partialLoads.join(', ')}. The other sections are
            current; treat the missing ones as unknown rather than clear.
          </span>
        </div>
      ) : null}

      {/* ------------------------------------------------------------------ */}
      {/*
        One row of tiles, each about 40px, each a link to the screen that can do something
        about it. Seven 100px tiles wrapped to two rows and pushed "Needs attention" — the
        table that says what is wrong — below the fold on the screen designed to be read in
        four seconds. "In flight" is gone (it restated a number one tile to its left, and it
        is now a clause on Runs today) and the two grant tiles are one. A stalled run does
        not get a tile either: it is a row in "Needs attention" directly below, with its
        reason, and a tile would be the same fact 100px higher with less of it.

        `unknown` carries the sentence naming the fetch that failed. The loader keeps
        whatever resolved, so a half-failed refresh used to render an authoritative grey 0 —
        and on a console that gates what goes to a lender, "we do not know" must be
        renderable and must not look like "all clear".
      */}
      <StatGrid>
        <StatTile
          label="Runs today"
          value={formatCount(todayRuns.length)}
          tone={todayRuns.length > 0 ? 'default' : 'muted'}
          glyph="▤"
          loading={loading}
          to="/runs"
          unknown={runsUnknown}
          hint={`${formatCount(sentToday)} sent · ${formatCount(inFlight.length)} in flight`}
          title={`Runs started today, ${istDate(today)} IST`}
        />
        <StatTile
          label="Stale"
          value={formatCount(staleToday)}
          tone={countTone(staleToday, 'warn')}
          glyph="▲"
          loading={loading}
          to="/runs?status=stale"
          unknown={runsUnknown}
          hint="Source missing or older than the window — nothing was sent"
          title="Stale is not a failure of ours: the export did not arrive (contract §8.3)"
        />
        <StatTile
          label="Failed"
          value={formatCount(failedToday)}
          tone={countTone(failedToday)}
          glyph="✕"
          terminalBad={failedToday > 0}
          loading={loading}
          to="/runs?status=failed"
          unknown={runsUnknown}
          hint="Today, IST"
        />
        <StatTile
          label="Links expiring"
          value={formatCount(expiringGrants.length)}
          tone={countTone(expiringGrants.length, 'warn')}
          glyph="◷"
          loading={loading}
          unknown={
            partialLoads.includes('links') &&
            'The link fetch failed on this refresh, so no expiry was counted. Unknown, not zero.'
          }
          hint={`Within 24 hours · ${formatCount(liveGrants.length)} live beyond that`}
          title="Counted from our expires_at, never from an S3 signature"
        />
        <StatTile
          label="Suppressed"
          value={formatCount(hardBounces.length)}
          tone={countTone(hardBounces.length)}
          glyph="⚑"
          terminalBad={hardBounces.length > 0}
          loading={loading}
          unknown={
            partialLoads.includes('recipients') &&
            'At least one client\u2019s recipient list did not load, so this is not a count of everyone bouncing. Unknown, not zero.'
          }
          hint={
            softBounces.length > 0
              ? `Hard bounces and complaints · ${formatCount(softBounces.length)} soft as well`
              : 'Hard bounces and complaints'
          }
        />
        <StatTile
          label="Unranked"
          value={formatCount(unrankedTotal)}
          tone={countTone(unrankedTotal, 'warn')}
          glyph="?"
          loading={loading}
          to="/dispositions"
          unknown={
            partialLoads.includes('dispositions') &&
            'At least one client\u2019s ranking did not load, so this is not a count of everything waiting. Unknown, not zero.'
          }
          hint="Outcomes waiting on a ranking decision"
        />
      </StatGrid>

      {/* ------------------------------------------------------------------ */}
      <Card
        title={
          <span className="cluster-tight">
            <span>Needs attention</span>
            {attention.length > 0 ? (
              <Badge tone="danger" glyph="!" title="Open items">
                {formatCount(attention.length)}
              </Badge>
            ) : null}
          </span>
        }
        flush
      >
        <Table<Attention>
          columns={attentionColumns}
          rows={attention}
          rowKey={(row) => row.id}
          loading={loading}
          flush
          rowTone={(row) => (row.kind === 'failed' ? 'danger' : 'warn')}
          empty={
            <EmptyState
              glyph="✓"
              title="Nothing failed or went stale"
              description={`No run in the last ${ATTENTION_DAYS} days ended failed or stale, and nothing is stuck in flight.`}
              action={
                <Link className="btn" style={{ textDecoration: 'none' }} to="/runs">
                  See all runs
                </Link>
              }
            />
          }
        />
      </Card>

      {/* ------------------------------------------------------------------ */}
      <Card
        title={
          <span className="cluster-tight">
            <span>Today&apos;s runs</span>
            <span className="dim small">{istDate(today)} IST</span>
          </span>
        }
        flush
        footer={
          nextSchedule ? (
            <span className="small muted">
              Next scheduled fire:{' '}
              <Link to="/schedules">{nextSchedule.name || nextSchedule.schedule_id}</Link> ·{' '}
              {istDateTime(nextSchedule.next_run_at)} ({timeAgo(nextSchedule.next_run_at, now)})
            </span>
          ) : undefined
        }
      >
        <Table<RunRow>
          columns={runColumns}
          rows={todayRuns}
          rowKey={(run) => run.run_id}
          loading={loading}
          flush
          rowTone={(run) =>
            run.status === 'failed'
              ? 'danger'
              : run.status === 'stale'
                ? 'warn'
                : coverageOf(run).isPartial
                  ? 'partial'
                  : 'default'
          }
          empty={
            <EmptyState
              title="No runs today yet"
              description={
                nextSchedule
                  ? `The next enabled schedule fires ${istDateTime(nextSchedule.next_run_at)} IST.`
                  : 'No enabled schedule has a next fire time recorded. Check the schedules screen.'
              }
              action={
                <Link className="btn btn-primary" style={{ textDecoration: 'none' }} to="/schedules">
                  Schedules
                </Link>
              }
              secondaryAction={
                <Link className="btn" style={{ textDecoration: 'none' }} to="/downloads">
                  Generate one now
                </Link>
              }
            />
          }
        />
      </Card>

      {/* ------------------------------------------------------------------ */}
      <Card
        title={
          <span className="cluster-tight">
            <span>Links expiring in the next 24 hours</span>
            {expiringGrants.length > 0 ? (
              <Badge tone="warn" glyph="◷">
                {formatCount(expiringGrants.length)}
              </Badge>
            ) : null}
          </span>
        }
      >
        <div className="stack">
          <GrantList
            grants={expiringGrants}
            loading={loading}
            showRun
            sortBy="expiry"
            emptyTitle="Nothing expires in the next 24 hours"
            emptyDescription={`Checked every link issued by the last ${GRANT_RUN_LIMIT} runs with a file, over the past ${GRANT_LOOKBACK_DAYS} days.`}
            onRevoked={(grant) => {
              setGrants((current) =>
                current.map((g) => (g.token_hash === grant.token_hash ? grant : g)),
              );
              toastRef.current.info('Dashboard updated', {
                description: 'That link no longer counts as expiring.',
              });
            }}
          />
          <p className="small muted">
            Re-issuing mints a new token; it does not extend this one. The file itself stays
            exactly where it is — expiring a link never deletes a client&apos;s data.
          </p>
        </div>
      </Card>

      {/* ------------------------------------------------------------------ */}
      <Card
        title={
          <span className="cluster-tight">
            <span>Recipients suppressed by bounces</span>
            {suppressed.length > 0 ? (
              <Badge tone={hardBounces.length > 0 ? 'danger' : 'warn'} glyph="⚑">
                {formatCount(suppressed.length)}
              </Badge>
            ) : null}
          </span>
        }
        flush
        footer={
          suppressed.length > 0 ? (
            <span className="small muted">
              A suppressed address is skipped at send time and nothing else on this console says
              so. Fix the address, or clear the state on the client, before the next run.
            </span>
          ) : undefined
        }
      >
        <Table<SuppressedRow>
          columns={suppressedColumns}
          rows={suppressed}
          rowKey={(row) => `${row.client_id}:${row.recipient_id}`}
          loading={loading}
          flush
          /* Not `dense`: --row-h-dense (24px) is for lists that carry no badge, and every
             row here carries a bounce state and a listed state. 28px it is. */
          rowTone={(row) =>
            row.bounce_state === 'hard' || row.bounce_state === 'complained' ? 'danger' : 'warn'
          }
          empty={
            partialLoads.includes('recipients') ? (
              <EmptyState
                tone="danger"
                title="Could not read every recipient list"
                description="At least one client's list did not load, so this table is not the answer to 'is anyone bouncing?'. Refresh before treating it as clear."
                action={
                  <Button variant="primary" onClick={manualRefresh}>
                    Retry
                  </Button>
                }
              />
            ) : (
              <EmptyState
                glyph="✓"
                title="Every recipient is deliverable"
                description="No hard bounce, soft bounce or complaint is recorded against any client's list."
              />
            )
          }
        />
      </Card>

      {/* ------------------------------------------------------------------ */}
      <Card
        title={
          <span className="cluster-tight">
            <span>Unranked dispositions</span>
            {unrankedTotal > 0 ? (
              <Badge tone="warn" glyph="?">
                {formatCount(unrankedTotal)}
              </Badge>
            ) : null}
          </span>
        }
      >
        {dispositions.length === 0 ? (
          <EmptyState
            glyph="✓"
            title="Every outcome seen is ranked"
            description="No recent run reported a disposition that is missing from its client's rank list."
          />
        ) : (
          <div className="stack">
            <p className="small muted">
              These values appeared in real exports and are in neither the ranked list nor the
              declared non-contact list. They are never silently sorted last — until someone
              rules, a borrower whose best call carried one of these is treated as unranked.
              Shown exactly as the source wrote them.
            </p>
            {dispositions.map((row) => (
              <div key={row.client_id} className="stack-tight">
                <div className="cluster">
                  <span className="strong">{row.client_name || row.client_id}</span>
                  <span className="dim mono xs">{row.client_id}</span>
                  <span className="grow" />
                  <Link
                    className="btn btn-sm btn-primary" style={{ textDecoration: 'none' }}
                    to={`/dispositions/${encodeURIComponent(row.client_id)}`}
                  >
                    Rank {formatCount(row.unranked.length)}
                  </Link>
                </div>
                <div className="cluster-tight">
                  {row.unranked.slice(0, 18).map((entry) => (
                    <Badge
                      key={entry.value}
                      tone="warn"
                      glyph="?"
                      title={
                        entry.seen_count
                          ? `Seen ${formatCount(entry.seen_count)} times${
                              entry.last_seen_at ? `, last ${istDateTime(entry.last_seen_at)}` : ''
                            }`
                          : 'Seen in a recent run'
                      }
                    >
                      <span className="mono">{entry.value}</span>
                      {entry.seen_count ? (
                        <span className="dim"> · {formatCount(entry.seen_count)}</span>
                      ) : null}
                    </Badge>
                  ))}
                  {row.unranked.length > 18 ? (
                    <span className="xs dim">
                      +{formatCount(row.unranked.length - 18)} more
                    </span>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <p className="xs dim">
        Refreshes every {Math.round(REFRESH_MS / 1000)} seconds while this tab is visible. All
        times are IST. Link states come from our own grant window, never from an S3 signature.
      </p>
    </div>
  );
}
